import type { Output } from "@offsite/shared";

/**
 * Renders an overnight Output: HTML in a sandboxed iframe, Markdown/Mermaid
 * as preformatted text (full Markdown/Mermaid renderers can come later).
 *
 * SANDBOX POLICY (live workshop):
 *   sandbox="allow-scripts" — and ONLY allow-scripts.
 *
 *   The KI-generated HTML can run JS to render charts/animations, but
 *   must NOT be allowed to:
 *   - allow-same-origin     → would let it read our cookies / localStorage
 *   - allow-forms           → no inadvertent POSTs back to us / 3rd parties
 *   - allow-modals          → no alert()/confirm() blocking the beamer
 *   - allow-popups          → no new windows opened during the reveal
 *   - allow-popups-to-escape-sandbox → no escape hatch
 *   - allow-top-navigation  → no `window.top.location = ...`
 *   - allow-pointer-lock    → no hijacking the cursor
 *   - allow-downloads       → no auto-download of files
 *
 * Effectively the iframe runs in an opaque origin — even if a model
 * hallucinates an `<iframe src=...>` or window.open, it just no-ops.
 */
export function OutputRenderer({ output }: { output: Output }) {
  if (output.contentType === "html") {
    return (
      <iframe
        title={`Output Team ${output.teamNumber}`}
        srcDoc={output.content}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        loading="lazy"
        className="output-frame"
      />
    );
  }
  return <pre className="output-pre">{output.content}</pre>;
}
